import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './apiConfig';
import logoImage from './assets/logo.png';

const LoginForm: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      setIsLoading(false);
      return;
    }

    try {
      const result = await login({ username: username.trim(), password });
      if (!result) {
        setError('Login failed');
      }
      // If successful, AuthContext will handle the redirect
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setResetMessage('');

    try {
      await axios.post(`${API_URL}/api/auth/password-reset-request`, {
        email: resetEmail
      });
      setResetMessage('If this email exists in our system, you will receive a password reset link.');
      setResetEmail('');
    } catch (error) {
      setResetMessage('An error occurred. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="bg-brand-gradient-screen flex items-center justify-center p-md">
    
      <div className="card w-full" style={{ maxWidth: '480px' }}>
        <div className="text-center mb-xl">
          <div className="flex justify-center mb-lg p-lg" style={{
            backgroundColor: 'white',
            borderRadius: 'var(--radius-lg)'
          }}>
            <img 
              src={logoImage} 
              alt="EvaluTool Logo" 
              className="logo-lg"
            />
          </div>
          <h1 className="mb-sm" style={{
            background: 'linear-gradient(45deg, #2563eb, #059669)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Welcome to EvaluTool
          </h1>
          <p className="text-secondary">
            Secure Procurement Evaluation Platform
          </p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              required
              className="input"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              className="input"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="card-error mb-lg">
              <h4 className="text-primary font-medium mb-xs">
                Authentication Error
              </h4>
              <p className="text-secondary">
                {error}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? (
              <>
                <div className="loading-spinner"></div>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
          
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() => setShowForgotPassword(!showForgotPassword)}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563eb',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Forgot your password?
            </button>
          </div>
        </form>

        {/* Forgot Password Form */}
        {showForgotPassword && (
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#1e293b' }}>
              Reset Password
            </h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#64748b' }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>
            
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="input"
                  disabled={resetLoading}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="submit"
                  disabled={resetLoading || !resetEmail}
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  {resetLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetMessage('');
                    setResetEmail('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
            
            {resetMessage && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#dcfce7',
                border: '1px solid #16a34a',
                borderRadius: '6px',
                color: '#166534',
                fontSize: '0.9rem'
              }}>
                {resetMessage}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm;